// ================================================
// CLARITY REPORTING — APP.JSX
// Main routing and auth wrapper
// ================================================

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import HomePage          from './pages/HomePage';
import PricingPage       from './pages/PricingPage';
import LoginPage         from './pages/LoginPage';
import RegisterPage      from './pages/RegisterPage';
import PaymentPage       from './pages/PaymentPage';
import DashboardPage     from './pages/DashboardPage';
import ReportPage        from './pages/ReportPage';
import SettingsPage      from './pages/SettingsPage';
import AgencyPage        from './pages/AgencyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SampleActionPlanPage from './pages/SampleActionPlanPage';
import AgencyLandingPage from './pages/AgencyLandingPage';
import SampleReportPage from './pages/SampleReportPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

import './styles/global.css';

// ---- Protected Route ----
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading&hellip;</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// ---- Public Route (redirect if logged in) ----
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading&hellip;</div>;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// ---- Agency-only Route ----
function AgencyRoute({ children }) {
  const { user } = useAuth();
  if (!['agency', 'agency_pro'].includes(user?.account_type)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              fontFamily: 'IBM Plex Sans, sans-serif',
              fontSize: '13px',
              borderRadius: '0',
              border: '1px solid #1a1a1a'
            },
            success: {
              iconTheme: { primary: '#1a7a1a', secondary: '#fff' }
            },
            error: {
              iconTheme: { primary: '#CC0000', secondary: '#fff' }
            }
          }}
        />
        <Routes>
          {/* Public routes */}
          <Route path="/"        element={<HomePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms"   element={<TermsOfServicePage />} />
          <Route path="/sample-report" element={<SampleReportPage />} />
          <Route path="/agencies" element={<AgencyLandingPage />} />
          <Route path="/sample-action-plan" element={<SampleActionPlanPage />} />

          <Route path="/login" element={
            <PublicRoute><LoginPage /></PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute><RegisterPage /></PublicRoute>
          } />

          {/* Payment — requires login, no active sub needed */}
          <Route path="/subscribe" element={
            <ProtectedRoute><PaymentPage /></ProtectedRoute>
          } />

          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute><DashboardPage /></ProtectedRoute>
          } />
          <Route path="/reports/:reportId" element={
            <ProtectedRoute><ReportPage /></ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute><SettingsPage /></ProtectedRoute>
          } />
          <Route path="/agency" element={
            <ProtectedRoute>
              <AgencyRoute><AgencyPage /></AgencyRoute>
            </ProtectedRoute>
          } />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
